import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarCheckIcon,
  DownloadSimpleIcon,
  FileXlsIcon,
  UploadSimpleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { ApiError, Button, Card, CardContent, CardHeader, CardTitle } from '@smena/ui';
import { embedCard, embedContent, embedHeader, type EmbeddableCardProps } from './embed';
import { BOOKINGS_TEMPLATE_URL, uploadBookingsFile, type IngestResult } from './api';

/**
 * «Разовый импорт броней»: загрузка списка из CSV/XLSX.
 *
 * Ручки `GET /api/staff/ingest/template.xlsx` и `POST /api/staff/ingest/upload` существовали
 * с самого начала, но интерфейса к ним не было ни одного. В мастере настройки на месте этого
 * блока стояла кнопка «Открыть /integrations» с подписью «шаблон - на странице импорта»,
 * а на самой странице интеграций такой карточки не было - человек уходил в никуда.
 *
 * Отчёт после загрузки показываем полностью, включая отклонённые строки: импорт почти всегда
 * частичный (объект не найден, даты пустые), и «загружено 12» без списка проблем создаёт
 * ложное ощущение, что перенеслось всё.
 */
export function BookingsImportCard({ embedded }: EmbeddableCardProps = {}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<IngestResult | null>(null);

  const uploadMut = useMutation({
    mutationFn: uploadBookingsFile,
    onSuccess: (res) => {
      setResult(res);
      const { inserted, updated } = res.report;
      if (inserted + updated > 0) {
        toast.success(`Загружено броней: ${inserted + updated}`);
      } else {
        toast.error('Ни одна строка не загрузилась - разберите отчёт ниже');
      }
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
    },
    onError: (e) => {
      setResult(null);
      toast.error(e instanceof ApiError ? e.message : 'Не удалось разобрать файл');
    },
  });

  function pick(file: File | undefined): void {
    if (!file) return;
    setResult(null);
    uploadMut.mutate(file);
    // Сбрасываем значение: иначе повторный выбор того же файла не даёт change-события,
    // и «загрузить ещё раз после правки» молча не работает.
    if (fileRef.current) fileRef.current.value = '';
  }

  const rejected = result ? result.report.skipped + result.report.errors.length : 0;

  return (
    <Card className={embedCard(embedded)}>
      <CardHeader className={embedHeader(embedded)}>
        <CardTitle className="flex items-center gap-2">
          <FileXlsIcon weight="bold" className="size-4 text-accent-fg" /> Разовый импорт из файла
        </CardTitle>
      </CardHeader>

      <CardContent className={embedContent(embedded, 'flex flex-col gap-3 text-sm')}>
        <p className="text-muted">
          Годится, чтобы перенести текущие брони при переезде. Постоянный поток лучше подключить
          через webhook или iCal - файл придётся загружать руками каждый раз.
        </p>

        {/* Отдельный абзац про RC, потому что это не «ещё один формат», а единственный способ
            получить историю: вебхук RC не отдаёт брони, созданные до его подключения, и молчит
            по броням с прошедшими датами. */}
        <div className="flex items-start gap-2 rounded-md border border-line bg-panel2/40 p-3 text-xs text-muted">
          <CalendarCheckIcon weight="bold" className="mt-0.5 size-4 shrink-0 text-accent-fg" />
          <div className="flex flex-col gap-1">
            <span className="text-text">Переезжаете с RealtyCalendar?</span>
            <span>
              Выгрузите брони в их кабинете: раздел «Клиенты и брони», вкладка «Брони», кнопка
              «Выгрузить в Excel». Загрузите файл сюда как есть - подгонять колонки не нужно.
            </span>
            <span>
              Так подтянется история, которой вебхук не отдаёт: он присылает только новые
              события и молчит по броням с прошедшими датами. Уже загруженные брони импорт не
              трогает, так что повторить его не страшно.
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Скачивание шаблона - обычная ссылка: сервер отдаёт файл с Content-Disposition,
              и браузер сохраняет его сам, без blob-обвязки. */}
          <Button variant="secondary" size="sm" asChild>
            <a href={BOOKINGS_TEMPLATE_URL} download>
              <DownloadSimpleIcon weight="bold" className="size-4" /> Скачать шаблон
            </a>
          </Button>
          <Button
            size="sm"
            disabled={uploadMut.isPending}
            onClick={() => fileRef.current?.click()}
          >
            <UploadSimpleIcon weight="bold" className="size-4" />
            {uploadMut.isPending ? 'Разбираем файл…' : 'Выбрать файл'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/xls,application/vnd.ms-excel"
            onChange={(e) => pick(e.target.files?.[0])}
          />
        </div>
        <p className="text-[11px] text-muted">
          CSV или XLSX по шаблону, либо выгрузка броней из кабинета RealtyCalendar. До 5 МБ.
        </p>

        {result ? (
          <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
            <div className="text-sm font-medium">
              Разобрано строк: {result.totalParsed} · загружено {result.report.inserted + result.report.updated}
              {rejected ? ` · не принято ${rejected}` : ''}
            </div>
            <div className="text-[11px] text-muted">
              новых {result.report.inserted} · обновлено {result.report.updated} · пропущено{' '}
              {result.report.skipped}
            </div>

            {result.parseErrors.length ? (
              <div className="flex flex-col gap-1 rounded-md border border-red/40 bg-red/10 p-2 text-[11px] text-red">
                <span className="font-medium">Файл разобран не полностью</span>
                {result.parseErrors.slice(0, 5).map((e, i) => (
                  <span key={i} className="break-words">
                    {e.row !== undefined ? `строка ${e.row}: ` : ''}
                    {e.message}
                  </span>
                ))}
                {result.parseErrors.length > 5 ? (
                  <span>и ещё {result.parseErrors.length - 5}</span>
                ) : null}
              </div>
            ) : null}

            {result.report.errors.length ? (
              <div className="flex flex-col gap-1 rounded-md border border-amber/40 bg-amber/10 p-2 text-[11px] text-amber">
                <span className="flex items-center gap-1 font-medium">
                  <WarningIcon weight="fill" className="size-3.5 shrink-0" /> Строки без объекта
                </span>
                {result.report.errors.slice(0, 5).map((e, i) => (
                  <span key={i} className="break-words">
                    {e.propertyHint ? `«${e.propertyHint}»: ` : ''}
                    {e.reason}
                  </span>
                ))}
                {result.report.errors.length > 5 ? (
                  <span>и ещё {result.report.errors.length - 5}</span>
                ) : null}
                <span className="mt-0.5">
                  Обычно это значит, что объект из файла ещё не заведён в Smena - создайте его на
                  шаге «Объекты» и загрузите файл повторно.
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
